import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import Select from '../../../../../../../../../components/common/select';
import Checkbox from '../../../../../../../../../components/common/checkbox';

import Button, {
  ButtonType,
} from '../../../../../../../../../components/common/button';
import { useDidMount } from '../../../../../../../../../components/common/effects';
import { Visibility } from '../../../../../../../../../types/common-types';
import { bbCodeToHtml } from '../../../../../../../../../utils/formUtils';
import './style.scss';

interface CalculatorValue {
  amount: string;
  selectedIban: string;
}
interface Labels {
  header: string;
  selectLoanAmount: string;
  legalNote: string;
  loanAmountSelected: string;
  minPayment: string;
  commision: string;
  interestRate: string;
  rrsoLabel: string;
  selectIban: string;
  period: string;
  consentATitle: string;
  consentBTitle1: string;
  consentBTitle2: string;
  consentCTitle1: string;
  consentCTitle2: string;
  consentDTitle1: string;
  consentDTitle2: string;
  consentETitle1: string;
  consentETitle2: string;
  consentFTitle1: string;
  consentFTitle2: string;
  consentGTitle1: string;
  consentGTitle2: string;
  submitButton: string;
  allCheckboxes: string;
  processing: string;
  availableLimit: string;
}
interface Iban {
  value: string;
  title: string;
}
interface Settings {
  interestRate: number;
  amount: {
    min: number;
    max: number;
    step: number;
    interest: number;
    period: number;
    commission: number;
    minimumPayout: number;
    disbursementCommission: number;
    rrso: number;
  };
  ibans: Iban[];
}

interface Props {
  id: string;
  // eslint-disable-next-line react/require-default-props
  visibility?: Visibility;
  value: CalculatorValue;
  settings: Settings;
  extra: {
    labels: Labels;
  };
  onSubmit: (id: string) => void;
  onChange: (
    value: {
      id: string;
      value: object;
      error: boolean;
    },
    callback?: () => void
  ) => void;
}

const Calculator: React.FC<Props> = ({
  id,
  visibility,
  extra,
  value,
  settings,
  onChange,
  onSubmit,
}) => {
  const { labels } = extra;
  const {
    header,
    legalNote,
    selectLoanAmount,
    loanAmountSelected,
    minPayment,
    commision,
    interestRate,
    rrsoLabel,
    selectIban,
    period,
    consentATitle,
    consentBTitle1,
    consentBTitle2,
    consentCTitle1,
    consentCTitle2,
    consentDTitle1,
    consentDTitle2,
    consentETitle1,
    consentETitle2,
    consentFTitle1,
    consentFTitle2,
    consentGTitle1,
    consentGTitle2,
    submitButton,
    allCheckboxes,
    availableLimit,
  } = labels;
  const [allCheckboxesValue, setAllCheckboxesValue] = useState(false);
  const [consentAValue, setConsentA] = useState(false);
  const [consentBValue, setConsentB] = useState(false);
  const [consentCValue, setConsentC] = useState(false);
  const [consentDValue, setConsentD] = useState(false);
  const [consentEValue, setConsentE] = useState(false);
  const [consentFValue, setConsentF] = useState(false);
  const [consentGValue, setConsentG] = useState(false);
  const [submitCompleted, setSubmitCompleted] = useState(false);
  const [processingLabel, setProcessingLabel] = useState('');
  const { amount: settingsAmount, ibans } = settings;
  const { amount, selectedIban = '' } = value;
  const [ibanFlag, setIban] = useState(!!selectedIban);

  const didMount = useDidMount();
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (didMount) onSubmit(id);
    }, 500);
    return () => clearTimeout(timeout);
  }, [value]);

  const handleChange = (key: string, val: string | boolean) => {
    const newData = {
      ...value,
      [key]: typeof val === 'boolean' ? val.toString() : val,
    };
    onChange({ id, value: newData, error: false });
  };

  return (
    <div
      id={id}
      data-cy="calculator"
      styleName={cn('calculator', {
        hidden: visibility === Visibility.Hidden,
      })}
    >
      <div styleName="calculator__header">
        <span>{header}</span>
      </div>
      <div styleName="calculator__divider" />
      <div styleName="calculator__editor">
        <div styleName="calculator__editor__item">
          <div styleName="calculator__editor__header">
            <span>{selectLoanAmount}</span>
            <span styleName="calculator__editor__value">
              <b>{`${amount} zł`}</b>
            </span>
          </div>
          <div styleName="calculator__editor__slider">
            <input
              type="range"
              data-cy-prop="value.amount"
              min={settingsAmount.min}
              max={settingsAmount.max}
              step={settingsAmount.step}
              value={amount}
              onChange={e => handleChange('amount', e.target.value)}
            />
          </div>
          <div styleName="calculator__editor__footer">
            <span>{`${settingsAmount.min}zł`}</span>
            <span>{`${settingsAmount.max}zł`}</span>
          </div>
        </div>
      </div>
      <div styleName="calculator__label micro">
        <span>{`${legalNote}`}</span>
      </div>
      <div styleName="calculator__summary">
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{loanAmountSelected}</span>
          <span styleName="calculator__row__val bold">
            {`${(parseFloat(amount) * (1 + settingsAmount.commission)).toFixed(
              2
            )}zł`}
          </span>
        </div>
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{availableLimit}</span>
          <span styleName="calculator__row__val bold">{`${amount}zł`}</span>
        </div>
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{minPayment}</span>
          <span styleName="calculator__row__val bold">
            {`${(settingsAmount.minimumPayout * 100).toFixed(1)}%`}
          </span>
        </div>
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{commision}</span>
          <span styleName="calculator__row__val bold">
            {`${(+amount * settingsAmount.commission).toFixed(2)}zł`}
          </span>
        </div>
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{interestRate}</span>
          <span styleName="calculator__row__val bold">
            {`${(settingsAmount.interest * 100).toFixed(1)}%`}
          </span>
        </div>
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{rrsoLabel}</span>
          <span styleName="calculator__row__val bold">
            {`${(settingsAmount.rrso * 100).toFixed(1)}%`}
          </span>
        </div>
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{period}</span>
          <span styleName="calculator__row__val bold">{`${settingsAmount.period}`}</span>
        </div>
      </div>
      <Select
        id={id}
        styleName="calculator__select"
        label={selectIban}
        value={selectedIban}
        options={ibans}
        onChange={e => {
          // eslint-disable-next-line no-unused-expressions
          e.value ? setIban(true) : setIban(false);
          return handleChange('selectedIban', e.value);
        }}
      />
      <div>
        <Checkbox
          id={id}
          visibility={visibility}
          value={allCheckboxesValue}
          onChange={e => {
            // eslint-disable-next-line no-unused-expressions
            if (e.value) {
              setAllCheckboxesValue(true);
              setConsentA(true);
              setConsentB(true);
              setConsentC(true);
              setConsentD(true);
              setConsentE(true);
              setConsentF(true);
              setConsentG(true);
            } else {
              setAllCheckboxesValue(false);
              setConsentA(false);
              setConsentB(false);
              setConsentC(false);
              setConsentD(false);
              setConsentE(false);
              setConsentF(false);
              setConsentG(false);
            }
          }}
        >
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(allCheckboxes),
            }}
          />
        </Checkbox>
        <Checkbox
          id={id}
          visibility={visibility}
          value={consentAValue}
          required
          onChange={e => {
            // eslint-disable-next-line no-unused-expressions
            e.value ? setConsentA(true) : setConsentA(false);
          }}
        >
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentATitle),
            }}
          />
        </Checkbox>
        <Checkbox
          id={id}
          visibility={visibility}
          value={consentBValue}
          required
          onChange={e => {
            // eslint-disable-next-line no-unused-expressions
            e.value ? setConsentB(true) : setConsentB(false);
          }}
        >
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentBTitle1),
            }}
          />
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentBTitle2),
            }}
          />
        </Checkbox>
        <Checkbox
          id={id}
          visibility={visibility}
          value={consentCValue}
          required
          onChange={e => {
            // eslint-disable-next-line no-unused-expressions
            e.value ? setConsentC(true) : setConsentC(false);
          }}
        >
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentCTitle1),
            }}
          />
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentCTitle2),
            }}
          />
        </Checkbox>
        <Checkbox
          id="contactPolicyLeadGrid"
          visibility={visibility}
          value={consentDValue}
          onChange={e => {
            // eslint-disable-next-line no-unused-expressions
            e.value ? setConsentD(true) : setConsentD(false);
          }}
        >
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentDTitle1),
            }}
          />
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentDTitle2),
            }}
          />
        </Checkbox>
        <Checkbox
          id="dataShareLeadGrid"
          visibility={visibility}
          value={consentEValue}
          onChange={e => {
            // eslint-disable-next-line no-unused-expressions
            e.value ? setConsentE(true) : setConsentE(false);
          }}
        >
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentETitle1),
            }}
          />
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentETitle2),
            }}
          />
        </Checkbox>
        <Checkbox
          id="contactPolicyLynks"
          visibility={visibility}
          value={consentFValue}
          onChange={e => {
            // eslint-disable-next-line no-unused-expressions
            e.value ? setConsentF(true) : setConsentF(false);
          }}
        >
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentFTitle1),
            }}
          />
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentFTitle2),
            }}
          />
        </Checkbox>
        <Checkbox
          id="dataShareLynks"
          visibility={visibility}
          value={consentGValue}
          onChange={e => {
            // eslint-disable-next-line no-unused-expressions
            e.value ? setConsentG(true) : setConsentG(false);
          }}
        >
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentGTitle1),
            }}
          />
          <span
            className="calculator__consent"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: bbCodeToHtml(consentGTitle2),
            }}
          />
        </Checkbox>
      </div>
      <Button
        id="calculatorButton"
        visibility={
          consentAValue &&
          consentBValue &&
          consentCValue &&
          ibanFlag &&
          !submitCompleted
            ? Visibility.Visible
            : Visibility.Hidden
        }
        type={ButtonType.Submit}
        label={submitButton}
        onClick={() => {
          handleChange(
            'calculatorButton',
            'contactPolicyLeadGrid:' +
              consentDValue +
              ',dataShareLeadGrid:' +
              consentEValue +
              ',contactPolicyLynks:' +
              consentFValue +
              ',dataShareLynks:' +
              consentGValue
          );
          setSubmitCompleted(true);
          setProcessingLabel('Procesowanie. Proszę czekać');
        }}
      />
      <div styleName="calculator__header">
        <span>{processingLabel}</span>
      </div>
    </div>
  );
};

export default Calculator;
